/**
 * Warehouse React Query Hooks
 *
 * React Query hooks for warehouse CRUD operations and KPIs
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import type {
  Warehouse,
  CreateWarehouseDto,
  UpdateWarehouseDto,
  WarehouseKpis,
} from '@lognai/plugin-warehouse';
import type { PaginationParams } from '@lognai/plugin-sdk';
import * as warehouseApi from '../api/warehouses';
import type { PaginatedWarehouseList } from '../api/warehouses';
import { warehouseKeys } from './query-keys';

/**
 * Get list of warehouses with pagination
 */
export const useWarehouses = (
  params?: PaginationParams,
  options?: Omit<UseQueryOptions<PaginatedWarehouseList>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: warehouseKeys.list(params),
    queryFn: () => warehouseApi.listWarehouses(params),
    ...options,
  });
};

/**
 * Get a single warehouse by ID
 */
export const useWarehouse = (
  id: string,
  options?: Omit<UseQueryOptions<Warehouse>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: warehouseKeys.detail(id),
    queryFn: () => warehouseApi.getWarehouse(id),
    enabled: !!id,
    ...options,
  });
};

/**
 * Get warehouse KPIs (total, active, capacity)
 */
export const useWarehouseKpis = (
  options?: Omit<UseQueryOptions<WarehouseKpis>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: warehouseKeys.kpis(),
    queryFn: () => warehouseApi.getWarehouseKpis(),
    ...options,
  });
};

/**
 * Create a new warehouse
 */
export const useCreateWarehouse = (
  options?: Omit<UseMutationOptions<Warehouse, Error, CreateWarehouseDto>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: warehouseApi.createWarehouse,
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate warehouse lists
      queryClient.invalidateQueries({ queryKey: warehouseKeys.lists() });
      // Invalidate KPIs
      queryClient.invalidateQueries({ queryKey: warehouseKeys.kpis() });
      // Call user's onSuccess if provided
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
};

/**
 * Update an existing warehouse
 */
export const useUpdateWarehouse = (
  options?: Omit<
    UseMutationOptions<Warehouse, Error, { id: string; dto: UpdateWarehouseDto }>,
    'mutationFn'
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }) => warehouseApi.updateWarehouse(id, dto),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate specific warehouse detail
      queryClient.invalidateQueries({ queryKey: warehouseKeys.detail(variables.id) });
      // Invalidate warehouse lists
      queryClient.invalidateQueries({ queryKey: warehouseKeys.lists() });
      // Call user's onSuccess if provided
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
};

/**
 * Delete a warehouse
 */
export const useDeleteWarehouse = (
  options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: warehouseApi.deleteWarehouse,
    onSuccess: (data, variables, onMutateResult, context) => {
      // Invalidate warehouse lists
      queryClient.invalidateQueries({ queryKey: warehouseKeys.lists() });
      // Invalidate KPIs
      queryClient.invalidateQueries({ queryKey: warehouseKeys.kpis() });
      // Remove specific warehouse from cache
      queryClient.removeQueries({ queryKey: warehouseKeys.detail(variables) });
      // Call user's onSuccess if provided
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    ...options,
  });
};
